<script>
import eventHelper from "@/mixins/eventHelper.js";
import apiHelper from "@/mixins/apiHelper.js";

export default {
  emits: ['eventCreated'],
  mixins: [
    eventHelper,
    apiHelper
  ],
  props: {
    agendas: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      saving: false,
      dialog: false,
      values: {
        name: '',
        allDay: false,
        date: null,
        time: null,
      },
      errors: {
        name: false,
        date: false,
        time: false,
        agenda: false,
        color: false,
      },
      selectedAgenda: null,
      selectedColor: null,
    }
  },
  computed: {
    agendasGrouped() {
      let agendas = [];
      this.agendas.users.forEach(user => {
        agendas.push({ label: user.name, value: 'user:'+user.id });
      });
      return agendas;
    },
    agendaColors() {
      let colors = [];
      this.agendas.color.forEach(color => {
        colors.push({ label: color.name, value: color.id });
      });
      return colors;
    },
    selectedAgendaIsOwn() {
      // Get currently logged-in user
      let user_id = drupalSettings.user.uid;
      // Check if the selected agenda is our own user agenda
      return this.selectedAgenda && this.selectedAgenda.value === 'user:'+user_id;
    },
  },
  methods: {
    closeDialog() {
      this.dialog = false;
    },
    openDialog(arg) {
      this.dialog = true;
      // Reset values
      this.values.name = '';
      // Set default to all day
      this.values.allDay = true;
      if (arg) {
        // Get the clicked date
        this.values.date = arg.date;

        if (arg.allDay) {
          // Set default value for the time
          let time = new Date();
          time.setHours(9);
          time.setMinutes(0);
          time.setSeconds(0);
          this.values.time = time;
        }
        else {
          // Disable all day
          this.values.allDay = false;
          // Set time to the clicked time
          this.values.time = arg.date;
        }
      }
      else {
        // Set default date to today
        this.values.date = new Date();
        // Set default value for the time
        let time = new Date();
        time.setHours(9);
        time.setMinutes(0);
        time.setSeconds(0);
        this.values.time = time;
      }
      // Set default selected agenda
      this.setDefaultSelectedAgenda();
    },
    /**
     * Create a new event by sending a POST request to the API.
     *
     * @returns {Promise<Response>}
     */
    async createEvent() {
      this.saving = true;
      // Clear previous toasts
      this.$toast.removeAllGroups();
      // Post new event to the API
      let payload = {};

      if (this.values.date === null) {
        this.errors.date = true;
        this.saving = false;
        return;
      }

      // Check if agenda is selected
      if (this.selectedAgenda === null) {
        this.errors.agenda = true;
        this.saving = false;
        return;
      }

      // Format the date for the API
      payload.date = this.formatJsDateForApi(this.values.date, this.values.time, this.values.allDay);

      // Add the name to the payload
      payload.name = this.values.name;
      payload.allDay = this.values.allDay;

      // Add selected agenda
      payload.agenda = this.selectedAgenda.value;
      if (this.selectedAgendaIsOwn) {
        payload.color = this.selectedColor ? this.selectedColor.value : null;
      }

      // Send request to API
      const response = await fetch('/api/event', {
        method: 'POST',
        credentials: "include", // Set to make sure the cookies are send with the request.
        headers: this.requestHeaders,
        body: JSON.stringify(payload)
      });

      this.saving = false;

      const status_code = response.status;

      if (status_code === 201) {
        let response_data = await response.json();
        // Close dialog
        this.closeDialog();
        // Emit event
        this.$emit('eventCreated');
        // Show message
        this.$toast.add({ severity: 'success', summary: 'Activiteit aangemaakt', detail: 'Activiteit is aangemaakt.', group: 'global', life: 4000 });
      }
      else {
        let response_data = await response.json();
        let details = 'De activiteit kon niet aangemaakt worden. ';
        if (response_data.errors) {
          // Add errors to the details
          for (const [key, value] of Object.entries(response_data.errors)) {
            details += value.path + ': ' + value.message;
            // Flag errors
            if (value.path === 'label') {
              this.errors.name = true;
            }
          }
        }
        // Show error message
        this.$toast.add({ severity: 'error', summary: 'Fout', detail: details, group: 'errors' });
      }

      return response
    },
    setDefaultSelectedAgenda() {
      // Get currently logged-in user
      let user_id = drupalSettings.user.uid;
      // Find the user agenda in the list
      this.agendasGrouped.forEach(item => {
        if (item.value === 'user:'+user_id) {
          // Preselect our own user agenda
          this.selectedAgenda = item;
        }
      });
    },
  }
}
</script>

<template>

  <Toast position="top-center" group="global" />
  <Toast position="top-center" group="errors" />

  <Dialog v-model:visible="dialog" modal header="Maak nieuw activiteit" :style="{ width: '32rem' }">
<!--    <span class="text-surface-500 dark:text-surface-400 block mb-8">Maak een nieuwe activiteit aan.</span>-->

    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label for="name">Naam activiteit *</label>
        <InputText id="name" v-model="values.name" autocomplete="off" :invalid="errors.name" @input="errors.name = false" />
        <Message v-if="errors.name" severity="error">Naam is verplicht</Message>
      </div>

      <div class="flex flex-col gap-1">
        <label for="date">Datum</label>
        <div class="flex items-center">
          <ToggleSwitch v-model="values.allDay" inputId="allDay" /> <label for="allDay" class="ml-1">Hele dag</label>
        </div>
        <div class="flex gap-2">
          <DatePicker id="date" v-model="values.date" showIcon dateFormat="dd-mm-yy" :invalid="errors.date" @input="errors.date = false" @date-select="errors.date = false" />
          <DatePicker v-if="!values.allDay" id="time" v-model="values.time" timeOnly />
        </div>
        <Message v-if="errors.date" severity="error">Datum is verplicht</Message>
      </div>

      <div class="flex flex-col gap-1" v-if="agendasGrouped.length > 1">
        <label for="date">Agenda *</label>
        <Select v-model="selectedAgenda" filter :options="agendasGrouped" optionLabel="label" placeholder="Selecteer een agenda" emptyMessage="Geen agendas gevonden" emptyFilterMessage="Geen resultaten" class="w-full md:w-56" @select-change-event="errors.agenda = false">
          <template #optiongroup="slotProps">
            <div class="flex items-center">
              <div>{{ slotProps.option.label }}</div>
            </div>
          </template>
        </Select>
        <Message v-if="errors.agenda" severity="error">Agenda is verplicht</Message>
      </div>

      <div class="flex flex-col gap-1" v-if="selectedAgendaIsOwn">
        <label for="date">Klant agenda (optioneel)</label>
        <Select v-model="selectedColor" filter showClear :options="agendaColors" optionLabel="label" placeholder="Standaard agenda" emptyMessage="Nog geen klant agendas aangemaakt" emptyFilterMessage="Geen resultaten" class="w-full md:w-56" @select-change-event="errors.color = false">
          <template #optiongroup="slotProps">
            <div class="flex items-center">
              <div>{{ slotProps.option.label }}</div>
            </div>
          </template>
        </Select>
      </div>

    </div>

    <div class="flex justify-end gap-2 mt-4">
      <Button type="button" label="Annuleren" severity="secondary" @click="closeDialog"></Button>
      <Button type="button" label="Opslaan" @click="createEvent" :disabled="saving" :loading="saving"></Button>
    </div>
  </Dialog>

</template>

<style>
 /* See agenda.scss for styling. Styling is here because we want the Gin/Bootstrap variables. */
</style>
